import { useEffect, useRef, useSyncExternalStore } from "react";

/**
 * Общее «сейчас» таблицы, обновляемое раз в секунду. Один таймер на всех
 * подписчиков, а не на строку: двести таймеров дают тот же результат ценой
 * двухсот пробуждений, а рассинхронизация между соседними строками видна
 * глазом. Читают его листья с подписью времени (`elapsed.tsx`), а не экран:
 * тик перерисовывает только их.
 *
 * Таймер заводится с первым подписчиком и гасится с последним.
 */
export const NOW_TICK_MS = 1000;

const listeners = new Set<() => void>();
let currentMs = Date.now();
let timer: ReturnType<typeof setInterval> | undefined;

function tick(): void {
  currentMs = Date.now();
  for (const listener of listeners) {
    listener();
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1) {
    // Значение могло простоять без подписчиков: берём свежее, React сверит
    // снимок сразу после подписки и перерисует лист, если оно разошлось.
    currentMs = Date.now();
    timer = setInterval(tick, NOW_TICK_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== undefined) {
      clearInterval(timer);
      timer = undefined;
    }
  };
}

function getSnapshot(): number {
  // Без подписчиков тика нет, и значение стоит: обновляем его не чаще раза в
  // тик, чтобы два вызова подряд за один рендер давали одно число.
  if (listeners.size === 0) {
    const now = Date.now();
    if (now - currentMs >= NOW_TICK_MS) {
      currentMs = now;
    }
  }
  return currentMs;
}

/**
 * Подпись, выведенная из часов. Снимок — готовая строка, и React сравнивает её
 * по значению: лист, чья подпись за тик не изменилась (возраст в минутах или
 * часах), не перерисовывается. `format` может быть inline-замыканием — снимок
 * пересчитывается на каждом вызове, кэш не нужен.
 */
export function useNowText(format: (nowMs: number) => string): string {
  return useSyncExternalStore(
    subscribe,
    () => format(getSnapshot()),
    () => format(Date.now()),
  );
}

const noopSubscribe = (): (() => void) => () => {};

/**
 * Общее «сейчас» в миллисекундах для экрана, которому время нужно в отборе,
 * а не в подписи (время до показа §82). Пока `enabled` ложно, на тикер экран
 * не подписывается и снимок стоит на нуле: отбор без настройки не
 * перерисовывается каждую секунду.
 *
 * Пока `paused` (скрытый экран, заморозка вида), тикер тоже не слушается:
 * экран держит последнее «сейчас», и отбор §82 не обнуляется. При снятии
 * паузы источник снова общий, а его снимок свежий: `getSnapshot` без
 * подписчиков обновляет значение, а с подписчиками оно не старше тика.
 */
export function useNowMs(enabled: boolean, paused = false): number {
  const heldRef = useRef(Date.now());
  const source = nowMsSource(enabled, paused, heldRef.current);
  const value = useSyncExternalStore(
    source.subscribe,
    source.getSnapshot,
    source.getServerSnapshot,
  );
  const live = enabled && !paused;
  useEffect(() => {
    if (live) {
      heldRef.current = value;
    }
  }, [live, value]);
  return value;
}

/**
 * Источник для `useNowMs`: при `enabled` и без паузы — общий тикер; при паузе
 * — пустая подписка и удержанное `heldMs`; без `enabled` — пустая подписка и
 * нулевой снимок. Вынесен, чтобы выбор подписки проверялся тестом без DOM:
 * статический рендер `subscribe` не вызывает.
 */
export function nowMsSource(
  enabled: boolean,
  paused = false,
  heldMs = 0,
): {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => number;
  getServerSnapshot: () => number;
} {
  if (!enabled) {
    return { subscribe: noopSubscribe, getSnapshot: () => 0, getServerSnapshot: () => 0 };
  }
  if (paused) {
    return {
      subscribe: noopSubscribe,
      getSnapshot: () => heldMs,
      getServerSnapshot: () => heldMs,
    };
  }
  return { subscribe, getSnapshot, getServerSnapshot: () => Date.now() };
}

/** Подписка и снимок «сейчас» — для тестов жизненного цикла таймера без React. */
export const nowStore = {
  subscribe,
  getSnapshot,
  tick,
  timerActive: () => timer !== undefined,
};
